import { z } from 'zod/v4';
import { ACTIONS } from '../types.js';

/** The closed set of platform actions (see `ACTIONS`). */
export const ActionSchema = z.enum(ACTIONS);
export type ActionType = z.infer<typeof ActionSchema>;

/** "{resourceType}:{action}", e.g. "order:read". The action must be a known one. */
export const PermissionKeySchema = z
  .string()
  .regex(
    new RegExp(`^[^:]+:(${ACTIONS.join('|')})$`),
    `permission must be "resourceType:action" with a known action (${ACTIONS.join(', ')})`,
  );

export const EffectSchema = z.enum(['allow', 'deny']);
export const ActorTypeSchema = z.enum(['user', 'service_account']);

export const RoleKeyParams = z.object({ key: z.string().min(1) });
export type RoleKeyParamsType = z.infer<typeof RoleKeyParams>;

export const RoleMemberParams = z.object({
  key: z.string().min(1),
  actorType: ActorTypeSchema,
  actorId: z.string().min(1),
});
export type RoleMemberParamsType = z.infer<typeof RoleMemberParams>;

export const UserParams = z.object({ userId: z.string().min(1) });
export type UserParamsType = z.infer<typeof UserParams>;

export const CatalogQuery = z.object({ resourceType: z.string().optional() });
export type CatalogQueryType = z.infer<typeof CatalogQuery>;

export const ExceptionsQuery = z.object({
  resourceType: z.string().min(1),
  resourceId: z.string().min(1),
});
export type ExceptionsQueryType = z.infer<typeof ExceptionsQuery>;

export const CreateRoleBody = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  inherits: z.array(z.string().min(1)).optional(),
});
export type CreateRoleBodyType = z.infer<typeof CreateRoleBody>;

/**
 * Identity-only patch: `key` is deliberately absent. The key is embedded in
 * every `iam_casbin_rules` tuple, so renaming it would have to rewrite the
 * tuple store — a display-name change must never do that.
 */
export const UpdateRoleBody = z
  .object({
    name: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
  })
  .refine(b => b.name !== undefined || b.description !== undefined, {
    message: 'name or description is required',
  });
export type UpdateRoleBodyType = z.infer<typeof UpdateRoleBody>;

export const GrantPermissionBody = z.object({
  permission: PermissionKeySchema,
  name: z.string().optional(),
});
export type GrantPermissionBodyType = z.infer<typeof GrantPermissionBody>;

export const RevokePermissionBody = z.object({
  permission: PermissionKeySchema,
});
export type RevokePermissionBodyType = z.infer<typeof RevokePermissionBody>;

export const AddParentBody = z.object({ parentKey: z.string().min(1) });
export type AddParentBodyType = z.infer<typeof AddParentBody>;

export const AssignMemberBody = z.object({
  actorType: ActorTypeSchema.default('user'),
  actorId: z.string().min(1),
  displayName: z.string().optional(),
});
export type AssignMemberBodyType = z.infer<typeof AssignMemberBody>;

export const CreateServiceAccountBody = z.object({ name: z.string().min(1) });
export type CreateServiceAccountBodyType = z.infer<
  typeof CreateServiceAccountBody
>;

/**
 * Replace a role's entire type-level permission set in one atomic call — the
 * permission-set editor commits the full grid, not incremental grant/revoke.
 */
export const SetRolePermissionsBody = z.object({
  permissions: z.array(PermissionKeySchema),
});
export type SetRolePermissionsBodyType = z.infer<typeof SetRolePermissionsBody>;

export const UpsertExceptionBody = z.object({
  userId: z.string().min(1),
  permission: PermissionKeySchema,
  resourceType: z.string().min(1),
  resourceId: z.string().min(1),
  effect: EffectSchema,
});
export type UpsertExceptionBodyType = z.infer<typeof UpsertExceptionBody>;

export const ClearExceptionBody = z.object({
  userId: z.string().min(1),
  permission: PermissionKeySchema,
  resourceType: z.string().min(1),
  resourceId: z.string().min(1),
});
export type ClearExceptionBodyType = z.infer<typeof ClearExceptionBody>;

export const RoleViewSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
});
export const RoleDetailSchema = z.object({
  role: RoleViewSchema,
  permissions: z.array(z.string()),
});
export const ActorSchema = z.object({ type: ActorTypeSchema, id: z.string() });
export const PermissionCatalogSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  key: z.string(),
  resourceType: z.string(),
  action: z.string(),
  name: z.string().nullable(),
});
export const ActorPermissionsSchema = z.object({
  permissions: z.array(z.string()),
  byResource: z.record(z.string(), z.array(z.string())),
});
export const RecordExceptionSchema = z.object({
  actorType: ActorTypeSchema,
  actorId: z.string(),
  permission: z.string(),
  effect: EffectSchema,
});
export const ActorExceptionSchema = z.object({
  permission: z.string(),
  resourceType: z.string(),
  resourceId: z.string(),
  effect: EffectSchema,
});
export const ResourceViewSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
});
export const ServiceAccountSchema = z.object({
  id: z.string(),
  name: z.string(),
  disabled: z.boolean(),
});
export const ActorWithRolesSchema = z.object({
  actorType: ActorTypeSchema,
  actorId: z.string(),
  displayName: z.string().nullable(),
  roleKeys: z.array(z.string()),
});
export const OkSchema = z.object({ ok: z.boolean() });

export const BlueprintResourceSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  actions: z.array(ActionSchema),
});
export const BlueprintFeatureGroupSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  resources: z.array(BlueprintResourceSchema),
});
export const PermissionTemplateSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  actions: z.array(ActionSchema),
});
export const PermissionBlueprintSchema = z.object({
  actions: z.array(ActionSchema),
  groups: z.array(BlueprintFeatureGroupSchema),
  templates: z.array(PermissionTemplateSchema),
});
